import apiClient from '@/shared/utils/api';
import { StaffLocationsResponse, StaffLocation, Location } from '@/shared/types/api';
import { BACKEND_CONFIG } from '@/config/backendConfig';

export interface AssignStaffToLocationRequest {
  staff_id: string;
  location_ids: string[];
  replace?: boolean; // If true, replace all assignments. If false, add to existing.
}

export interface AssignStaffToLocationResponse {
  message: string;
  assigned_locations: StaffLocation[];
  total_locations: number;
}

class LocationService {
  private baseUrl = '/facilities';

  /**
   * Get locations accessible to the authenticated staff member.
   * Used for populating location selector in frontend.
   */
  async getStaffLocations(): Promise<StaffLocationsResponse> {
    const endpoint = BACKEND_CONFIG.endpoints.staffLocations(BACKEND_CONFIG.isFastAPI);
    const response = await apiClient.get(endpoint);
    return response.data;
  }

  /**
   * Assign a staff member to one or more locations.
   * Requires appropriate permissions.
   */
  async assignStaffToLocations(
    data: AssignStaffToLocationRequest
  ): Promise<AssignStaffToLocationResponse> {
    const response = await apiClient.post(`${this.baseUrl}/staff/locations/assign/`, data);
    return response.data;
  }

  /**
   * Remove a staff member's assignment from a specific location.
   * Requires appropriate permissions.
   */
  async removeStaffFromLocation(staffId: string, locationId: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/staff/${staffId}/locations/${locationId}/`);
  }

  /**
   * Get all locations (for admin/management views).
   */
  async getAllLocations(params?: {
    is_active?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{ results: Location[]; total: number; page: number; pages: number }> {
    const response = await apiClient.get(`${this.baseUrl}/locations/`, { params });
    return response.data;
  }

  /**
   * Get a specific location by ID.
   */
  async getLocation(locationId: string): Promise<Location> {
    const response = await apiClient.get(`${this.baseUrl}/locations/${locationId}/`);
    return response.data;
  }

  /**
   * Utility: Format location for display in selector.
   */
  formatLocationLabel(location: StaffLocation): string {
    const primaryLabel = location.is_primary ? ' (Primary)' : '';
    return `${location.name}${primaryLabel}`;
  }

  /**
   * Utility: Get primary location from list.
   */
  getPrimaryLocation(locations: StaffLocation[]): StaffLocation | null {
    return locations.find((loc) => loc.is_primary) || null;
  }
}

export const locationService = new LocationService();
export default locationService;
